import { authApiService } from './authService';

export const paymentService = {
  getConnectionConfig: async () => {
    return authApiService.get('/payments/connection-config');
  },

  createCheckoutSession: async (checkoutData) => {
    const response = await authApiService.post('/payments/checkout-session', checkoutData);
    const payload = response?.data ?? response;
    if (payload?.body?.data) return payload.body.data;
    if (payload?.data) return payload.data;
    return payload;
  },

  getPaymentStatus: async (paymentId) => {
    return authApiService.get(`/payments/${paymentId}/status`);
  },

  authorizeQuickBooks: async () => {
    // Redirect to QuickBooks authorization page with correct API prefix
    window.location.href = `${process.env.REACT_APP_API_URL || 'https://app.virtualscribe.ai/api'}/payments/oauth/authorize`;
  },
  
  // Handle QuickBooks OAuth callback
  handleQuickBooksCallback: async (urlParams) => {
    // Extract code and realmId from URL parameters
    const params = new URLSearchParams(urlParams);
    const code = params.get('code');
    const realmId = params.get('realmId');
    
    if (!code || !realmId) {
      throw new Error('Missing required OAuth parameters');
    }
    
    return authApiService.get(`/payments/oauth/callback?code=${code}&realmId=${realmId}`);
  },

  // Refresh QuickBooks OAuth token
  refreshQuickBooksToken: async (realmId) => {
    if (!realmId) {
      throw new Error('RealmId is required to refresh token');
    }
    
    return authApiService.get(`/payments/oauth/refresh/${realmId}`);
  },

  // STEP 1: Create card token for 3DS payment flow
  createCardToken: async (cardInfo, realmId) => {
    if (!cardInfo || !realmId) {
      throw new Error('Card information and realm ID are required');
    }
    
    return authApiService.post('/payments/card/token', {
      cardInfo,
      realmId
    });
  },

  // STEP 2: Initiate payment with 3DS support
  initiateCardPayment: async (paymentData) => {
    const {
      cardToken,
      amount,
      customerName,
      customerEmail,
      description,
      realmId,
      billingAddress,
      planKey,
      billingPeriod,
      seatCount,
    } = paymentData;
    
    if (!cardToken || !amount || !customerName || !customerEmail || !realmId) {
      throw new Error('Missing required payment fields');
    }
    
    return authApiService.post('/payments/card/payment', {
      cardToken,
      amount,
      customerName,
      customerEmail,
      description,
      realmId,
      billingAddress,
      planKey,
      billingPeriod,
      seatCount,
    });
  },

  // STEP 3: Check 3DS authentication status
  check3DSStatus: async (paymentId, chargeId) => {
    if (!paymentId || !chargeId) {
      throw new Error('Payment ID and Charge ID are required');
    }
    
    return authApiService.get(`/payments/card/3ds-status/${paymentId}/${chargeId}`);
  },

  // Process a general payment (for backwards compatibility)
  processPayment: async (paymentData) => {
    return authApiService.post('/payments/process', paymentData);
  },

  // Get payment by ID
  getPaymentById: async (paymentId) => {
    return authApiService.get(`/payments/${paymentId}`);
  },

  shareReceipt: async (paymentId, { email, message } = {}) => {
    return authApiService.post(`/payments/${paymentId}/share-receipt`, {
      email,
      message,
    });
  },

  // Get payments by customer email
  getPaymentsByEmail: async (email) => {
    return authApiService.get(`/payments/customer/${email}`);
  },

  // Get recent payments (admin/management only)
  getRecentPayments: async (limit = 10) => {
    return authApiService.get(`/payments?limit=${limit}`);
  },

  getAdminStats: async () => {
    return authApiService.get('/payments/admin/stats');
  },

  listAdminPayments: async (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return authApiService.get(`/payments/admin/list?${query}`);
  },

  syncPaymentStatus: async (paymentId) => {
    return authApiService.post(`/payments/admin/${paymentId}/sync`);
  },

  syncPendingPayments: async (processor = 'stripe') => {
    return authApiService.post('/payments/admin/sync-pending', { processor });
  },

  // Mark payment as completed (admin/management only)
  markPaymentCompleted: async (paymentId) => {
    return authApiService.put(`/payments/${paymentId}/complete`);
  },

  // Mark payment as refunded (admin/management only)
  markPaymentRefunded: async (paymentId) => {
    return authApiService.put(`/payments/${paymentId}/refund`);
  },

  // Helper method to handle complete 3DS payment flow
  processCardPaymentWith3DS: async (cardInfo, paymentDetails, callbacks = {}) => {
    try {
      const { onTokenCreated, on3DSRequired, onPaymentCompleted } = callbacks;
      
      // Step 1: Create card token
      if (onTokenCreated) onTokenCreated('Creating secure card token...');
      
      const tokenResponse = await paymentService.createCardToken(cardInfo, paymentDetails.realmId);
      console.log(tokenResponse, "tokenResponse");
      
      // Handle axios response structure - check if response has data property
      const responseData = tokenResponse.data || tokenResponse;
      
      if (!responseData.success) {
        throw new Error(responseData.error || 'Failed to create card token');
      }
      
      // Step 2: Initiate payment
      const paymentData = {
        ...paymentDetails,
        cardToken: responseData.cardToken
      };
      
      const initiateResponse = await paymentService.initiateCardPayment(paymentData);
      
      // Handle axios response structure for initiate response
      const initiateData = initiateResponse.data || initiateResponse;
      
      if (!initiateData.success) {
        throw new Error(initiateData.error || 'Failed to initiate payment');
      }
      
      // Step 3: Handle 3DS if required
      if (initiateData.requires3DS) {
        if (on3DSRequired) {
          on3DSRequired({
            threeDSecureUrl: initiateData.threeDSecureUrl,
            paymentId: initiateData.paymentId,
            chargeId: initiateData.chargeId,
            returnUrl: initiateData.nextAction?.returnUrl
          });
        }
        
        // Return payment info for 3DS handling
        return {
          success: true,
          requires3DS: true,
          paymentId: initiateData.paymentId,
          chargeId: initiateData.chargeId,
          threeDSecureUrl: initiateData.threeDSecureUrl,
          message: '3D Secure authentication required'
        };
      }
      
      // Payment completed without 3DS
      if (onPaymentCompleted) {
        onPaymentCompleted({
          paymentId: initiateData.paymentId,
          chargeId: initiateData.chargeId,
          status: 'completed'
        });
      }
      
      return {
        success: true,
        requires3DS: false,
        paymentId: initiateData.paymentId,
        chargeId: initiateData.chargeId,
        status: 'completed',
        message: 'Payment completed successfully'
      };
      
    } catch (error) {
        console.error('Card payment error:', error);
        throw error;
    }
  },

  // Validate card information before processing
  validateCardInfo: (cardInfo) => {
    const errors = [];
    
    if (!cardInfo.number || cardInfo.number.replace(/\s/g, '').length < 13) {
      errors.push('Valid card number is required');
    }
    
    if (!cardInfo.expMonth || cardInfo.expMonth < 1 || cardInfo.expMonth > 12) {
      errors.push('Valid expiration month is required');
    }
    
    if (!cardInfo.expYear || cardInfo.expYear < new Date().getFullYear()) {
      errors.push('Valid expiration year is required');
    }
    
    if (!cardInfo.cvc || cardInfo.cvc.length < 3) {
      errors.push('Valid security code is required');
    }
    
    if (!cardInfo.name || cardInfo.name.trim().length < 2) {
      errors.push('Cardholder name is required');
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  },

  // Format card number for display
  formatCardNumber: (number) => {
    const cleaned = number.replace(/\D/g, '');
    const formatted = cleaned.replace(/(\d{4})(?=\d)/g, '$1 ');
    return formatted;
  },

  // Get card type from number
  getCardType: (number) => {
    const cleaned = number.replace(/\D/g, '');
    
    if (/^4/.test(cleaned)) return 'visa';
    if (/^5[1-5]/.test(cleaned)) return 'mastercard';
    if (/^3[47]/.test(cleaned)) return 'amex';
    if (/^6(?:011|5)/.test(cleaned)) return 'discover';
    
    return 'unknown';
  }
};
